/**
 * AuthX MobileApp `/api/apps/*` — kontrakt oparty o resultCode / responseCode / code (+ httpStatusCode),
 * nie o OLIVS SuccessYN.
 */

export function isAuthxAppsRequestUrl(url) {
  if (!url || typeof url !== "string") return false;
  return url.includes("/api/apps/") || url.includes("authx-api.");
}

/** Kod biznesowy z JSON (camelCase / PascalCase). */
export function normalizeAuthxResultCode(data) {
  if (!data || typeof data !== "object") return null;
  const d = data;
  const rc =
    d.resultCode ??
    d.ResultCode ??
    d.responseCode ??
    d.ResponseCode ??
    d.code ??
    d.Code;
  return typeof rc === "string" ? rc.trim().toUpperCase() : null;
}

/** Sukces body przy HTTP 200 z AuthX (OK lub para kluczy sesji). */
export function isAuthxAppsSuccessBody(data) {
  if (!data || typeof data !== "object") return false;
  const d = data;
  if (normalizeAuthxResultCode(data) === "OK") return true;
  const http = Number(d.httpStatusCode ?? d.HttpStatusCode ?? Number.NaN);
  const hasPair =
    Boolean(d.accessKey || d.AccessKey) && Boolean(d.refreshKey || d.RefreshKey);
  if (hasPair && (!Number.isFinite(http) || http === 200)) return true;
  return false;
}

/** HTTP + body success for AuthX ops that may return successYN and/or resultCode. */
export function isAuthxAppsOperationSuccess(httpOk, data) {
  if (!data || typeof data !== "object") return Boolean(httpOk);
  const yn = data.successYN ?? data.SuccessYN;
  if (yn === "N") return false;
  if (yn === "Y") return true;
  if (isAuthxAppsExplicitFailureBody(data)) return false;
  if (isAuthxAppsSuccessBody(data)) return true;
  return Boolean(httpOk);
}

/** Jawny błąd kontraktu AuthX (nie-OK lub HTTP>=400 w payloadzie). */
export function isAuthxAppsExplicitFailureBody(data) {
  if (!data || typeof data !== "object") return false;
  const rc = normalizeAuthxResultCode(data);
  if (rc !== null && rc !== "OK") return true;
  const d = data;
  const http = Number(d.httpStatusCode ?? d.HttpStatusCode ?? Number.NaN);
  return Number.isFinite(http) && http >= 400;
}

const MFA_NOT_ENABLED_MESSAGE = "2fa is not enabled";
const SESSION_EXPIRED_MESSAGES = new Set([
  "your session expired. please log in again.",
  "your session expired. please login again.",
]);

function getAuthxErMsgText(body) {
  if (!body || typeof body !== "object") return "";
  const erMsg = body.ErMsg ?? body.erMsg;
  if (!erMsg || typeof erMsg !== "object") return "";
  const text = erMsg.Text ?? erMsg.text;
  const header = erMsg.Header ?? erMsg.header;
  if (typeof text !== "string" || !text.trim()) return "";
  const trimmed = text.trim();
  return typeof header === "string" && header.trim()
    ? `${header.trim()}: ${trimmed}`
    : trimmed;
}

function getAuthxPlainTextMessage(body) {
  if (typeof body === "string") return body.trim();
  if (!body || typeof body !== "object") return "";
  const message =
    body.ErrorMessage ??
    body.errorMessage ??
    body.message ??
    body.Message;
  return typeof message === "string" ? message.trim() : "";
}

/** Snackbar / UI copy from AuthX body (plain text, JSON string, or object). */
export function getAuthxAppsErrorMessage(body, fallback = "") {
  const erMsgText = getAuthxErMsgText(body);
  if (erMsgText) return erMsgText;
  const message = getAuthxPlainTextMessage(body);
  return message || fallback;
}

/** AuthX `/api/apps/*` may return JSON or plain text (e.g. verify-mfa). */
export async function parseAuthxAppsResponseBody(response) {
  const text = await response.text();
  const trimmed = text.trim();
  if (!trimmed) return null;
  try {
    return JSON.parse(trimmed);
  } catch {
    return trimmed;
  }
}

export function isAuthxMfaNotEnabledBody(body) {
  if (typeof body === "string") {
    return body.trim().toLowerCase() === MFA_NOT_ENABLED_MESSAGE;
  }
  if (!body || typeof body !== "object") return false;
  const message = getAuthxPlainTextMessage(body);
  return message.toLowerCase() === MFA_NOT_ENABLED_MESSAGE;
}

export function isAuthxSessionExpiredBody(body) {
  return SESSION_EXPIRED_MESSAGES.has(
    getAuthxPlainTextMessage(body).toLowerCase(),
  );
}

/** AuthX bodies handled by login/MFA navigation — skip global error toast. */
export function isAuthxAppsFlowHandledBody(body) {
  return isAuthxMfaNotEnabledBody(body) || isAuthxSessionExpiredBody(body);
}
